import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import matchService from '../../service/matchService';
import cover from '../../assets/cover.jpg';
import './home.css';

const fallbackSignals = [
    {
        id: 'sig-1',
        matchup: 'India vs Australia',
        phase: '14.2 overs',
        leadingChoice: 'India momentum',
        crowdPct: 78,
        swing: '+9% in last 3 overs',
        volume: '3.2k community votes'
    },
    {
        id: 'sig-2',
        matchup: 'England vs Pakistan',
        phase: '8.5 overs',
        leadingChoice: 'Pakistan comeback',
        crowdPct: 64,
        swing: '+5% after wicket',
        volume: '1.9k community votes'
    },
    {
        id: 'sig-3',
        matchup: 'South Africa vs New Zealand',
        phase: '17.0 overs',
        leadingChoice: 'High-scoring finish',
        crowdPct: 71,
        swing: '+7% in power surge',
        volume: '2.4k community votes'
    }
];

const hashToRange = (value, min, max) => {
    const base = String(value || 'cp').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return min + (base % (max - min + 1));
};

const buildSignalFromMatch = (match) => {
    const teamA = match.team_a || match?.title?.split('vs')?.[0]?.trim() || 'Team A';
    const teamB = match.team_b || match?.title?.split('vs')?.[1]?.trim() || 'Team B';
    const crowdPct = hashToRange(match._id, 55, 82);
    const swingRaw = hashToRange(`${match._id}-swing`, -6, 11);
    const volumeRaw = hashToRange(`${match._id}-votes`, 12, 49) / 10;
    const leadingChoice = crowdPct >= 67 ? `${teamA} edge` : `${teamB} fightback`;
    const phase = match?.content || match?.published_at || 'Live session';

    return {
        id: match._id,
        matchup: `${teamA} vs ${teamB}`,
        phase,
        leadingChoice,
        crowdPct,
        swing: `${swingRaw >= 0 ? '+' : ''}${swingRaw}% recent swing`,
        volume: `${volumeRaw.toFixed(1)}k community votes`
    };
};

export default function Home() {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [signals, setSignals] = useState(fallbackSignals);

    const spotlight = useMemo(() => signals[0] || fallbackSignals[0], [signals]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const loggedIn = Boolean(token);
        setIsLoggedIn(loggedIn);

        if (!loggedIn) {
            setSignals(fallbackSignals);
            return;
        }

        const loadLiveSignals = async () => {
            try {
                const response = await matchService.getMatches(1, 5, 'Live');
                const liveMatches = response?.data || [];
                if (liveMatches.length > 0) {
                    setSignals(liveMatches.slice(0, 3).map(buildSignalFromMatch));
                } else {
                    setSignals(fallbackSignals);
                }
            } catch (error) {
                setSignals(fallbackSignals);
            }
        };

        loadLiveSignals();
    }, []);

    const quickFlow = [
        {
            title: 'Watch Crowd Pulse',
            desc: 'See where community sentiment shifts over-by-over.',
            cta: 'Open Live Sessions',
            action: () => navigate('/match')
        },
        {
            title: 'Cast Your Call',
            desc: 'Vote on match direction and compare against live momentum.',
            cta: 'Start Predicting',
            action: () => navigate('/prediction')
        },
        {
            title: 'Learn From Trends',
            desc: 'Read what changed and why users moved their picks.',
            cta: 'Read Insights',
            action: () => navigate('/blogs')
        }
    ];

    const trustSignals = [
        { label: 'Live users in session', value: '12.4k' },
        { label: 'Signals refreshed', value: 'every 15 sec' },
        { label: 'Avg trend confidence', value: '73%' },
        { label: 'Community votes today', value: '84k+' }
    ];

    return (
        <div className="dashboard">
            <section className="dash-hero">
                <div className="hero-content fade-rise">
                    <p className="eyebrow">Live Cricket Sentiment Analytics</p>
                    <h1>Open one live match. Understand where the crowd is moving before every session turns.</h1>
                    <p className="hero-sub">
                        Your dashboard starts with the strongest live signal, then guides users through voting trends,
                        swing alerts, and outcome tracking in one flow.
                    </p>
                    <div className="hero-actions">
                        <button className="dash-btn dash-btn--solid" onClick={() => navigate('/match')}>Enter Live Session</button>
                        <button className="dash-btn dash-btn--ghost" onClick={() => navigate(isLoggedIn ? '/prediction' : '/register')}>
                            {isLoggedIn ? 'Track My Calls' : 'Join Free & Vote'}
                        </button>
                    </div>
                    <div className="hero-highlights">
                        {trustSignals.map((signal) => (
                            <div key={signal.label} className="highlight">
                                <span className="highlight-value">{signal.value}</span>
                                <span className="highlight-label">{signal.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="hero-visual fade-rise" style={{ backgroundImage: `url(${cover})` }}>
                    <div className="overlay-card">
                        <div className="overlay-row">
                            <span>Live spotlight</span>
                            <strong>{spotlight.matchup}</strong>
                        </div>
                        <div className="overlay-grid">
                            <div>
                                <p>Leading signal</p>
                                <h3>{spotlight.leadingChoice}</h3>
                            </div>
                            <div>
                                <p>Community confidence</p>
                                <h3>{spotlight.crowdPct}%</h3>
                            </div>
                        </div>
                        <div className="overlay-row muted">
                            <span>{spotlight.phase}</span>
                            <strong>{spotlight.swing}</strong>
                        </div>
                    </div>
                </div>
            </section>

            <section className="pulse-strip">
                {signals.map((signal, idx) => (
                    <article key={signal.id} className="pulse-card fade-rise" style={{ animationDelay: `${0.06 * (idx + 1)}s` }}>
                        <div className="pulse-top">
                            <span className="pulse-tag">Session {idx + 1}</span>
                            <span className="pulse-phase">{signal.phase}</span>
                        </div>
                        <h3>{signal.matchup}</h3>
                        <p>{signal.leadingChoice}</p>
                        <div className="progress-wrap" aria-label={`${signal.crowdPct}% community confidence`}>
                            <div className="progress-fill" style={{ width: `${signal.crowdPct}%` }} />
                        </div>
                        <div className="pulse-bottom">
                            <span>{signal.crowdPct}% backing</span>
                            <span>{signal.volume}</span>
                        </div>
                    </article>
                ))}
            </section>

            <section className="quick-actions">
                {quickFlow.map((item, idx) => (
                    <div key={item.title} className="qa-card fade-rise" style={{ animationDelay: `${0.08 * (idx + 1)}s` }}>
                        <h3>{item.title}</h3>
                        <p>{item.desc}</p>
                        <button className="dash-btn dash-btn--light" onClick={item.action}>{item.cta}</button>
                    </div>
                ))}
            </section>
        </div>
    );
}
